import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  KeyOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useCapability } from '@digiwedge/access-control-pages';
import { useIdp } from '../auth/IdpProvider.js';

type ScimConfigResponse = {
  tenantId: string;
  enabled: boolean;
  tokenLast4?: string;
  tokenCreatedAt?: string;
  tokenRotatedAt?: string;
  lastSyncAt?: string;
  createdAt: string;
  updatedAt: string;
};

type RotateTokenResponse = {
  token: string;
  tokenLast4: string;
  tokenCreatedAt: string;
  tokenRotatedAt: string;
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

export default function TenantScimConfigPage(): React.ReactElement {
  const { message } = App.useApp();
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const idp = useIdp();
  const canRead = useCapability({ feature: 'TENANT_MGMT', action: 'read' });
  const canUpdate = useCapability({ feature: 'TENANT_MGMT', action: 'update' });
  const canDelete = useCapability({ feature: 'TENANT_MGMT', action: 'delete' });

  const [config, setConfig] = useState<ScimConfigResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState('');
  const [tokenMeta, setTokenMeta] = useState<RotateTokenResponse | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  const baseUrl = useMemo(() => {
    const raw = idp.axios.defaults.baseURL;
    if (raw && raw.trim().length > 0) return raw.replace(/\/$/, '');
    if (typeof window !== 'undefined') return window.location.origin;
    return '';
  }, [idp.axios.defaults.baseURL]);

  const scimEndpoint = useMemo(() => {
    if (!tenantId) return '';
    return `${baseUrl}/api/scim/v2/tenants/${tenantId}`;
  }, [baseUrl, tenantId]);

  const copyToClipboard = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} copied to clipboard.`);
    } catch {
      message.error(`Unable to copy ${label.toLowerCase()}.`);
    }
  }, [message]);

  const loadConfig = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await idp.axios.get(`/api/admin/scim/config/${tenantId}`);
      setConfig(res.data as ScimConfigResponse);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setConfig(null);
      } else {
        setError('Unable to load SCIM configuration.');
      }
    } finally {
      setLoading(false);
    }
  }, [idp.axios, tenantId]);

  useEffect(() => {
    if (!canRead.allowed) return;
    void loadConfig();
  }, [canRead.allowed, loadConfig]);

  const handleToggle = async (enabled: boolean) => {
    if (!tenantId || !canUpdate.allowed) return;
    setLoading(true);
    try {
      if (!config) {
        await idp.axios.post('/api/admin/scim/config', { tenantId, enabled });
      } else {
        await idp.axios.put(`/api/admin/scim/config/${tenantId}`, { enabled });
      }
      message.success(`SCIM ${enabled ? 'enabled' : 'disabled'} for tenant.`);
      await loadConfig();
    } catch {
      message.error('Unable to update SCIM configuration.');
    } finally {
      setLoading(false);
    }
  };

  const handleRotateToken = async () => {
    if (!tenantId || !canUpdate.allowed) return;
    setTokenLoading(true);
    try {
      const res = await idp.axios.post(`/api/admin/scim/config/${tenantId}/token`);
      const payload = res.data as RotateTokenResponse;
      setTokenValue(payload.token);
      setTokenMeta(payload);
      setTokenModalOpen(true);
      message.success('New SCIM token generated.');
      await loadConfig();
    } catch {
      message.error('Unable to rotate SCIM token.');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!tenantId || !canUpdate.allowed) return;
    setTokenLoading(true);
    try {
      await idp.axios.delete(`/api/admin/scim/config/${tenantId}/token`);
      message.success('SCIM token revoked.');
      await loadConfig();
    } catch {
      message.error('Unable to revoke SCIM token.');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!tenantId || !canDelete.allowed) return;
    setLoading(true);
    try {
      await idp.axios.delete(`/api/admin/scim/config/${tenantId}`);
      message.success('SCIM configuration deleted.');
      setConfig(null);
    } catch {
      message.error('Unable to delete SCIM configuration.');
    } finally {
      setLoading(false);
    }
  };

  if (!tenantId) {
    return (
      <Alert
        type="error"
        title="Tenant not found"
        description="Missing tenant ID in route."
        showIcon
      />
    );
  }

  return (
    <Card
      title="SCIM Provisioning"
      extra={
        <Space>
          <Button onClick={() => navigate('/tenants')}>Back to Tenants</Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadConfig()}
            loading={loading}
            disabled={!canRead.allowed}
          >
            Refresh
          </Button>
        </Space>
      }
      style={{ margin: 24 }}
      loading={loading}
    >
      <Typography.Paragraph type="secondary">
        Configure SCIM provisioning for this tenant and manage the bearer token used by your
        identity provider.
      </Typography.Paragraph>

      {error && (
        <Alert
          type="error"
          title="Error"
          description={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {!error && !loading && !config && (
        <Alert
          type="info"
          title="No SCIM configuration yet"
          description="Enable SCIM to create a configuration and generate a token for this tenant."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Descriptions bordered size="middle" column={1}>
        <Descriptions.Item label="Tenant ID">{tenantId}</Descriptions.Item>
        <Descriptions.Item label="SCIM base URL">
          <Space>
            <Typography.Text code>{scimEndpoint}</Typography.Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(scimEndpoint, 'SCIM base URL')}
            />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          {config?.enabled ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Token">
          {config?.tokenLast4 ? (
            <Space>
              <Tag color="blue">•••• {config.tokenLast4}</Tag>
              <Typography.Text type="secondary">
                Rotated {formatDate(config.tokenRotatedAt)}
              </Typography.Text>
            </Space>
          ) : (
            <Typography.Text type="secondary">No token generated</Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Last sync">
          {formatDate(config?.lastSyncAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {formatDate(config?.createdAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Updated">
          {formatDate(config?.updatedAt)}
        </Descriptions.Item>
      </Descriptions>

      <Space style={{ marginTop: 24 }} wrap>
        <Button
          type={config?.enabled ? 'default' : 'primary'}
          icon={<SyncOutlined />}
          onClick={() => handleToggle(!config?.enabled)}
          disabled={!canUpdate.allowed}
        >
          {config?.enabled ? 'Disable SCIM' : 'Enable SCIM'}
        </Button>
        <Button
          icon={<KeyOutlined />}
          onClick={handleRotateToken}
          loading={tokenLoading}
          disabled={!canUpdate.allowed || !config}
        >
          Rotate Token
        </Button>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={handleRevokeToken}
          loading={tokenLoading}
          disabled={!canUpdate.allowed || !config?.tokenLast4}
        >
          Revoke Token
        </Button>
        <Button
          danger
          onClick={() =>
            Modal.confirm({
              title: 'Delete SCIM configuration?',
              content: 'This removes the SCIM settings for this tenant.',
              okText: 'Delete',
              okButtonProps: { danger: true },
              onOk: handleDeleteConfig,
            })
          }
          disabled={!canDelete.allowed || !config}
        >
          Delete Configuration
        </Button>
      </Space>

      <Modal
        title="New SCIM Token"
        open={tokenModalOpen}
        onCancel={() => setTokenModalOpen(false)}
        onOk={() => {
          setTokenModalOpen(false);
          setTokenValue('');
          setTokenMeta(null);
        }}
        okText="Done"
      >
        <Typography.Paragraph>
          Copy this token now. It will not be shown again.
        </Typography.Paragraph>
        <Input.TextArea value={tokenValue} readOnly autoSize={{ minRows: 3, maxRows: 6 }} />
        <Space style={{ marginTop: 12 }}>
          <Button
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(tokenValue, 'SCIM token')}
          >
            Copy Token
          </Button>
          {tokenMeta?.tokenLast4 && (
            <Typography.Text type="secondary">•••• {tokenMeta.tokenLast4}</Typography.Text>
          )}
        </Space>
      </Modal>
    </Card>
  );
}
